// CuentasListXL - Sprint 44 Fase 3 Vista 8.
// Lista plan de cuentas con filtros (q, tipo, activo, solo_hojas) +
// XLTable con indent visual por nivel + acciones Editar / Activar/Inactivar.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ensureKeyframes, injectCSS } from '../../../design/motion';
import useCuentasXL from '../hooks/useCuentasXL';
import * as svc from '../services/contabilidadService';
import XLTable from '../../../components/xl/XLTable';
import XLInput from '../../../components/xl/XLInput';
import XLSelect from '../../../components/xl/XLSelect';
import XLBadge from '../../../components/xl/XLBadge';
import XLChip from '../../../components/xl/XLChip';
import XLCheck from '../../../components/xl/XLCheck';
import { TIPO_VARIANT, TIPO_OPTIONS, ACTIVO_OPTIONS } from '../utils';

const CSS_ID = 'xl-cuentas-list-css';
const CSS = `
.xl-page { padding: 24px; font-family: var(--xl-font-sans); color: var(--xl-color-text) }
.xl-page__head { display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; margin-bottom: 16px; flex-wrap: wrap; }
.xl-page__title { font-size: 22px; font-weight: 600; margin: 0 }
.xl-page__sub { font-size: 13px; color: var(--xl-color-text-secondary); margin: 2px 0 0 }
.xl-btn {
  font: 500 13px/1 var(--xl-font-sans); padding: 10px 16px;
  border-radius: var(--xl-radius-md); border: 1px solid transparent; cursor: pointer;
}
.xl-btn--primary { background: var(--xl-color-primary); color: var(--xl-color-text-inverse) }
.xl-btn--primary:hover { background: var(--xl-color-primary-hover) }
.xl-btn:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }

.xl-filters {
  display: grid; grid-template-columns: 2fr 1fr 1fr auto; gap: 12px;
  background: var(--xl-color-surface); border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg); padding: 16px; margin-bottom: 16px;
  box-shadow: var(--xl-shadow-sm);
  align-items: end;
}
@media (max-width: 900px) { .xl-filters { grid-template-columns: 1fr } }

.xl-banner {
  padding: 10px 14px; margin-bottom: 12px;
  border-radius: var(--xl-radius-md); font-size: 13px;
}
.xl-banner--ok  { background: rgba(5,150,105,0.10); color: var(--xl-color-success); border: 1px solid rgba(5,150,105,0.28) }
.xl-banner--err { background: rgba(220,38,38,0.10); color: var(--xl-color-danger);  border: 1px solid rgba(220,38,38,0.28) }

.xl-mono { font-family: var(--xl-font-mono); font-size: 12px }
.xl-cuenta-actions { display: flex; gap: 4px; justify-content: flex-end }
`;

function useDebouncedSetter(setter, delay) {
  const [local, setLocal] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setter(local), delay);
    return () => clearTimeout(t);
  }, [local, delay, setter]);
  return [local, setLocal];
}

export default function CuentasListXL() {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);
  const navigate = useNavigate();
  const cx = useCuentasXL();
  const [searchLocal, setSearchLocal] = useDebouncedSetter(cx.setQ, 300);

  const onToggle = async (c) => {
    const accion = c.activo ? 'Inactivar' : 'Activar';
    if (!window.confirm(accion + ' cuenta ' + c.codigo + ' ' + c.nombre + '?')) return;
    try {
      await svc.toggleCuenta(c.id, !c.activo);
      cx.pushMsg('ok', 'Cuenta ' + (c.activo ? 'inactivada' : 'activada'));
      cx.load();
    } catch (e) {
      cx.pushMsg('err', (e && e.message) || 'Error');
    }
  };

  const columns = useMemo(() => ([
    { key: 'codigo', label: 'Codigo',
      render: (c) => (
        <span
          className="xl-mono"
          style={{ paddingLeft: ((c.nivel || 1) - 1) * 12 + 'px' }}
        >
          {c.codigo}
        </span>
      ) },
    { key: 'nombre', label: 'Nombre' },
    { key: 'tipo', label: 'Tipo',
      render: (c) => (
        <XLBadge variant={TIPO_VARIANT[c.tipo] || 'neutral'}>
          {c.tipo}
        </XLBadge>
      ) },
    { key: 'nivel', label: 'Nivel', align: 'center',
      render: (c) => <span className="xl-mono">{c.nivel}</span> },
    { key: 'naturaleza', label: 'Naturaleza',
      render: (c) => c.naturaleza || '\u2014' },
    { key: 'activo', label: 'Estado',
      render: (c) => (
        <XLBadge variant={c.activo ? 'success' : 'neutral'} dot>
          {c.activo ? 'Activa' : 'Inactiva'}
        </XLBadge>
      ) },
    { key: '_acc', label: '', align: 'right',
      render: (c) => (
        <span className="xl-cuenta-actions" onClick={(e) => e.stopPropagation()}>
          <XLChip onClick={() => navigate('/contabilidad/cuentas/' + c.id)}>Editar</XLChip>
          {c.activo
            ? <XLChip variant="danger"  onClick={() => onToggle(c)}>Inactivar</XLChip>
            : <XLChip variant="success" onClick={() => onToggle(c)}>Activar</XLChip>}
        </span>
      ) },
    // onToggle captura cx con métodos estables; añadirlo a deps invalidaría
    // useMemo en cada render y haría inútil el cache de columnas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [navigate]);

  return (
    <div className="xl-page">
      <header className="xl-page__head">
        <div>
          <h2 className="xl-page__title">Plan de cuentas</h2>
          <p className="xl-page__sub">NIIF / PUC \u00b7 multi-tenant.</p>
        </div>
        <button type="button" className="xl-btn xl-btn--primary"
          onClick={() => navigate('/contabilidad/cuentas/nueva')}>
          + Nueva cuenta
        </button>
      </header>

      <section className="xl-filters" aria-label="Filtros de cuentas">
        <XLInput type="search" label="Buscar"
          placeholder="Codigo o nombre..."
          value={searchLocal}
          onChange={(e) => setSearchLocal(e.target.value)} />
        <XLSelect label="Tipo" value={cx.tipo}
          onChange={(e) => cx.setTipo(e.target.value)}
          options={TIPO_OPTIONS} />
        <XLSelect label="Estado" value={cx.activo}
          onChange={(e) => cx.setActivo(e.target.value)}
          options={ACTIVO_OPTIONS} />
        <XLCheck checked={cx.solo_hojas}
          onChange={(v) => cx.setSoloHojas(v)}
          label="Solo hojas (nivel 3+)" />
      </section>

      {cx.msg && (
        <div className={'xl-banner ' + (cx.msg.kind === 'ok' ? 'xl-banner--ok' : 'xl-banner--err')}
          role={cx.msg.kind === 'ok' ? 'status' : 'alert'}
          onClick={cx.clearMsg}>
          {cx.msg.text}
        </div>
      )}
      {cx.err && <div className="xl-banner xl-banner--err" role="alert">{cx.err}</div>}

      <XLTable
        ariaLabel="Plan de cuentas"
        columns={columns}
        data={cx.items}
        loading={cx.loading}
        onRowClick={(c) => navigate('/contabilidad/cuentas/' + c.id)}
        rowKey="id"
        emptyMessage="Sin cuentas. Ejecuta el seed del plan NIIF." />
    </div>
  );
}
